import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    TextField
} from '@material-ui/core';
import HotelFieldInput from './HotelFieldInput';
import BookingFieldDate from './atom/BookingFieldDate';
import {
    isPhoneField,
    isEmailField,
    formatPhone,
    validatePhone,
    validateEmail
} from '../helpers/phoneUtils';
import { safeJsonParse } from '../utils';

export default function GuestFieldInput({
    field,
    values,
    errors,
    setFieldValue,
    handleEventValue
}) {
    const { required } = field;

    const guestField = values?.guest_field_info?.find(e => e.id === field.id);
    const guestFieldError = errors?.[field.id] || '';

    const visibleIslands = safeJsonParse(values?.company?.client_config, {}).visibleIslands || [];

    const currentValue =
        typeof guestField?.value === 'string' ? guestField.value : guestField?.value ?? '';

    switch (field.type) {
        case 'boolean':
            return (
                <FormControl error={!!guestFieldError}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id={`boolean-${field.id}`}
                                checked={Boolean(guestField?.value)}
                                onChange={ev =>
                                    handleEventValue(
                                        values,
                                        setFieldValue,
                                        field,
                                        ev.target.checked
                                    )
                                }
                                required={required}
                            />
                        }
                        label={required ? `${field.name} *` : field.name}
                    />
                    {guestFieldError && <FormHelperText>{guestFieldError}</FormHelperText>}
                </FormControl>
            );
        case 'number':
            return (
                <TextField
                    id={`text-${field.id}`}
                    label={field.name}
                    required={required}
                    value={guestField?.value || ''}
                    type="number"
                    onChange={ev => handleEventValue(values, setFieldValue, field, ev.target.value)}
                    fullWidth
                    error={!!guestFieldError}
                    helperText={guestFieldError || ''}
                />
            );
        case 'date':
            return (
                <BookingFieldDate
                    field={field}
                    required={required}
                    value={guestField?.value || null}
                    onChange={newDate =>
                        handleEventValue(
                            values,
                            setFieldValue,
                            field,
                            newDate ? newDate.format('YYYY-MM-DD') : null
                        )
                    }
                />
            );
        case 'hotel':
            return (
                <HotelFieldInput
                    value={typeof guestField?.value === 'number' ? guestField?.value : ''}
                    onChange={value => handleEventValue(values, setFieldValue, field, value)}
                    visibleIslands={visibleIslands}
                    required={required}
                    label={field.name}
                    error={!!guestFieldError}
                    helperText={guestFieldError || ''}
                />
            );
        default: {
            const isPhone = isPhoneField(field.name);
            const isEmail = isEmailField(field.name);

            return (
                <TextField
                    id={`text-${field.id}`}
                    label={field.name}
                    required={required}
                    value={currentValue}
                    type={isPhone ? 'tel' : isEmail ? 'email' : 'text'}
                    onChange={ev => handleEventValue(values, setFieldValue, field, ev.target.value)}
                    onBlur={() => {
                        if (isPhone && currentValue) {
                            const formatted = formatPhone(currentValue);
                            if (formatted !== currentValue) {
                                handleEventValue(values, setFieldValue, field, formatted);
                            }
                        }
                    }}
                    error={!!guestFieldError}
                    helperText={guestFieldError || ''}
                    fullWidth
                />
            );
        }
    }
}

/**
 * Validates guest field values, including phone/email detection by field name.
 * Returns an errors object keyed by field id.
 */
export function validateGuestFields(guestFieldInfo, guestFields) {
    const errors = {};
    guestFields.forEach(field => {
        const guestField = (guestFieldInfo || []).find(e => e.id === field.id);
        const value = guestField?.value || '';

        if (field.required && !value) {
            errors[field.id] = `${field.name} is required.`;
            return;
        }

        if (!value) {
            return;
        }

        if (isPhoneField(field.name)) {
            const phoneError = validatePhone(value);
            // validatePhone returns 'Phone is required.' for empty — skip that for optional fields
            if (phoneError && phoneError !== 'Phone is required.') {
                errors[field.id] = phoneError;
            }
        }

        if (isEmailField(field.name)) {
            const emailError = validateEmail(value);
            if (emailError) {
                errors[field.id] = emailError;
            }
        }
    });
    return errors;
}
